import { Vector2 } from "../../../common/vector2.js";
import { randomRange, randomSign } from "../../../common/common_funcs.js";
import { randomPointInCircle } from "../../../common/visual_fx_common_funcs.js";
import { CatmulromPrecomputed } from "../../utils/catmulrom_precomputed.js";
import { Game } from "../../../game.js";
import { TopCounterLayout } from "../../../ui/common_ui_elements/coin_counter_pill.js";
import { VfxCoinFlyBatch } from "./vfx_coin_fly_batch.js";
import { VfxParticleNormalBatch, VfxParticleAdditiveBatch } from "../particle/vfx_particle_batch.js";
import { Vfx2DRuntime } from "../../vfx_2d_runtime.js";
import { PayloadEmitter } from "../../core/emitters/payload_emitter.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { VfxTaskRunner } from "../../core/runtime/vfx_task_runner.js";
import { VfxCoinCounterParticlesController } from "./vfx_coin_counter_particles_controller.js";
import { VfxCoinCounterGlowController } from "./vfx_coin_counter_glow_controller.js";

export class VfxCoinFly
{
  public static readonly COIN_SIZE              : number = 100;
  public static readonly MIN_LIFESPAN           : number = 700;
  public static readonly MAX_LIFESPAN           : number = 1000;
  public static readonly MIN_DELAY              : number = 30;
  public static readonly MAX_DELAY              : number = 60;
  public static readonly MIN_AMPLITUDE          : number = 50;
  public static readonly MAX_AMPLITUDE          : number = 350;
  public static readonly SPAWN_CIRCLE_RADIUS    : number = 25;
  public static readonly MIN_ANGLE              : number = -0.15;
  public static readonly MAX_ANGLE              : number = 0.15;
  public static readonly ACCELERATION_POWER     : number = 2.2; // > 1.0 creates accelerating motion (ease-in)

  // Main glow trail constants
  public static readonly TRAIL_IMG_ID           : ImgID = ImgID.CircleGlow2;
  public static readonly TRAIL_COLOR            = { x: 1.0, y: 0.8, z: 0.3 };
  public static readonly TRAIL_PARTICLE_LIFESPAN: number = 300;
  public static readonly TRAIL_START_SIZE       : number = 100;
  public static readonly TRAIL_END_SIZE         : number = 50;
  public static readonly TRAIL_START_ALPHA      : number = 0.3;
  public static readonly TRAIL_END_ALPHA        : number = 0;
  public static readonly TRAIL_BREAKPOINT       : number = 0.0;
  public static readonly TRAIL_RATE_OVER_DIST   : number = 0.05;

  // Sparkle particles constants (CircleGlow2)
  public static readonly SPARK_IMG_ID           : ImgID = ImgID.SparkGlow2;
  public static readonly SPARK_COLOR            = { x: 1.0, y: 0.8, z: 0.3 };
  public static readonly SPARK_PARTICLE_LIFESPAN: number = 300;
  public static readonly SPARK_SIZE             : number = 30;
  public static readonly SPARK_START_ALPHA      : number = 1;
  public static readonly SPARK_END_ALPHA        : number = 0;
  public static readonly SPARK_BREAKPOINT       : number = 0.2;
  public static readonly SPARK_CIRCLE_RADIUS    : number = 50;
  public static readonly SPARK_EMISSION_COUNT   : number = 3;
  public static readonly SPARK_RATE_OVER_DIST   : number = 0.008;

  // Target icon glow/particles effect constants
  public static readonly TARGET_EFFECT_MIN_DURATION   : number = 200;
  public static readonly TARGET_EFFECT_EXTRA_DURATION : number = 200;

  public static burst(
    batch: VfxCoinFlyBatch,
    from: Vector2,
    to: Vector2,
    count: number,
    start_time: number,
    speed: number = 1
  ): number
  {
    const spawn_center = from ?? new Vector2( Game.getScreenWidth() / 2, Game.getScreenHeight() / 2 );
    const target = to ?? TopCounterLayout.coinIconCenter();
    const scale = LayoutScaler.getScale();
    const normal_trail_batch = Vfx2DRuntime.onOverlayFront( VfxParticleNormalBatch );
    const additive_trail_batch = Vfx2DRuntime.onOverlayFront( VfxParticleAdditiveBatch );

    let first_arrival_time = Infinity;
    let last_arrival_time = 0;

    for ( let i = 0; i < count; i++ )
    {
      const spawn_pos = randomPointInCircle( spawn_center, VfxCoinFly.SPAWN_CIRCLE_RADIUS * scale );
      const amplitude_direction = randomSign();
      const amplitude = randomRange( VfxCoinFly.MIN_AMPLITUDE, VfxCoinFly.MAX_AMPLITUDE ) * amplitude_direction * scale;

      const precomputed = CatmulromPrecomputed.calcPrecomputed( spawn_pos, target, amplitude, { mode: "perpendicular_normal" } );
      const duration = randomRange( VfxCoinFly.MIN_LIFESPAN, VfxCoinFly.MAX_LIFESPAN ) / speed;
      const coin_delay = start_time + i * randomRange( VfxCoinFly.MIN_DELAY, VfxCoinFly.MAX_DELAY ) / speed;
      const arrival_time = coin_delay + duration;

      if ( arrival_time < first_arrival_time ) first_arrival_time = arrival_time;
      if ( arrival_time > last_arrival_time ) last_arrival_time = arrival_time;

      const coin_size = VfxCoinFly.COIN_SIZE;
      const anim_offset = Math.floor( randomRange( 0, 16 ) );
      const angle = randomRange( VfxCoinFly.MIN_ANGLE, VfxCoinFly.MAX_ANGLE );
      const ease_fn = ( t: number ) => Math.pow( t, VfxCoinFly.ACCELERATION_POWER );

      batch.spawnCoin(
        spawn_pos,
        precomputed,
        coin_size,
        angle,
        anim_offset,
        duration,
        coin_delay,
        ease_fn
      );

      // 1. Main smooth glow trail (CircleGlow3)
      normal_trail_batch.addEmitter( new PayloadEmitter(
        spawn_pos,
        coin_delay,
        {
          color: VfxCoinFly.TRAIL_COLOR,
          precomputed,
          ease: ease_fn,
          config: {
            img_id: VfxCoinFly.TRAIL_IMG_ID,
            particle_life_span: VfxCoinFly.TRAIL_PARTICLE_LIFESPAN,
            start_size: VfxCoinFly.TRAIL_START_SIZE * scale,
            end_size: VfxCoinFly.TRAIL_END_SIZE * scale,
            start_alpha: VfxCoinFly.TRAIL_START_ALPHA,
            end_alpha: VfxCoinFly.TRAIL_END_ALPHA,
            breakpoint: VfxCoinFly.TRAIL_BREAKPOINT,
          }
        },
        duration,
        { rate_over_distance: VfxCoinFly.TRAIL_RATE_OVER_DIST / scale }
      ) );

      // 2. Sparkling scatter particles (CircleGlow2)
      additive_trail_batch.addEmitter( new PayloadEmitter(
        spawn_pos,
        coin_delay,
        {
          color: VfxCoinFly.SPARK_COLOR,
          precomputed,
          ease: ease_fn,
          config: {
            img_id: VfxCoinFly.SPARK_IMG_ID,
            particle_life_span: VfxCoinFly.SPARK_PARTICLE_LIFESPAN,
            start_size: VfxCoinFly.SPARK_SIZE * scale,
            end_size: VfxCoinFly.SPARK_SIZE * scale,
            start_alpha: VfxCoinFly.SPARK_START_ALPHA,
            end_alpha: VfxCoinFly.SPARK_END_ALPHA,
            breakpoint: VfxCoinFly.SPARK_BREAKPOINT,
            circle_radius: VfxCoinFly.SPARK_CIRCLE_RADIUS * scale,
          }
        },
        duration,
        { rate_over_distance: VfxCoinFly.SPARK_RATE_OVER_DIST / scale, count: VfxCoinFly.SPARK_EMISSION_COUNT }
      ) );
    }

    // 3. Coin particles and the rotating glow at the target icon start when
    // the first coin arrives and fade after the last one lands.
    const target_icon_size = VfxCoinFly.COIN_SIZE * scale;
    const target_effect_duration = Math.max(
      VfxCoinFly.TARGET_EFFECT_MIN_DURATION,
      last_arrival_time - first_arrival_time + VfxCoinFly.TARGET_EFFECT_EXTRA_DURATION
    );

    VfxTaskRunner.addTask(
      new VfxCoinCounterParticlesController( target, target_icon_size, target_effect_duration, first_arrival_time )
    );
    VfxTaskRunner.addTask(
      new VfxCoinCounterGlowController(
        target,
        target_icon_size,
        first_arrival_time,
        target_effect_duration,
        true
      )
    );

    return last_arrival_time;
  }
}
